import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { VALID_FIELDS } from './FullAddress';
import PostalCodeInput from './PostalCodeInput';
import { setErrorMessages, getShowAllErrors } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { jsx as _jsx } from "react/jsx-runtime";
const AddressLine1 = ({
  value,
  onChange,
  disabled,
  isShippingAddress = false
}) => {
  const dispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const isError = (hasBeenBlurred || showAllErrors) && !value;
  useEffect(() => {
    dispatch(setErrorMessages({
      [`empty${isShippingAddress ? 'Shipping' : 'Billing'}AddressLine1`]: {
        isError
      }
    }));
  }, [isError, dispatch, isShippingAddress]);
  const errorMessage = useGetErrorMessagesAndTranslations().get('emptyAddressLine1');
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "payments.fullAddress.address"
    }),
    required: true,
    "data-test-id": "address-line-1-field",
    error: isError,
    validationMessage: isError && errorMessage,
    children: /*#__PURE__*/_jsx(TextInput, {
      value: value,
      onChange: onChange,
      placeholder: I18n.text('payments.fullAddress.addressPlaceholder'),
      disabled: disabled,
      "data-test-id": "address-line-1-input",
      use: "on-dark",
      onBlur: () => setHasBeenBlurred(true)
    })
  });
};
const AddressLine2 = ({
  value,
  onChange,
  disabled
}) => /*#__PURE__*/_jsx(FormControl, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "payments.fullAddress.address2"
  }),
  "data-test-id": "address-line-2-field",
  children: /*#__PURE__*/_jsx(TextInput, {
    value: value,
    onChange: onChange,
    placeholder: I18n.text('payments.fullAddress.address2Placeholder'),
    disabled: disabled,
    "data-test-id": "address-line-2-input",
    use: "on-dark"
  })
});
const City = ({
  value,
  onChange,
  disabled,
  isUS,
  isShippingAddress = false
}) => {
  const dispatch = useDispatch();
  const showAllErrors = useSelector(getShowAllErrors);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const isError = (hasBeenBlurred || showAllErrors) && !value;
  const usErrorKey = isShippingAddress ? 'emptyShippingCity' : 'emptyBillingCity';
  const nonUsErrorKey = isShippingAddress ? 'emptyShippingCityTownVillage' : 'emptyBillingCityTownVillage';
  useEffect(() => {
    dispatch(setErrorMessages({
      [usErrorKey]: {
        isError: isUS && isError
      },
      [nonUsErrorKey]: {
        isError: !isUS && isError
      }
    }));
  }, [isError, dispatch, isUS, usErrorKey, nonUsErrorKey]);
  const errorMessage = useGetErrorMessagesAndTranslations().get(isUS ? 'emptyCity' : 'emptyCityTownVillage');
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: isUS ? 'payments.fullAddress.city' : 'payments.fullAddress.cityTownVillage'
    }),
    required: true,
    "data-test-id": "city-field",
    error: isError,
    validationMessage: isError && errorMessage,
    children: /*#__PURE__*/_jsx(TextInput, {
      value: value,
      onChange: onChange,
      disabled: disabled,
      "data-test-id": "city-input",
      use: "on-dark",
      onBlur: () => setHasBeenBlurred(true)
    })
  });
};
const FullAddressField = ({
  disabled,
  isUS,
  onChange,
  type,
  value,
  isShippingAddress = false
}) => {
  switch (type) {
    case VALID_FIELDS.ADDRESS_LINE_1:
      return /*#__PURE__*/_jsx(AddressLine1, {
        value: value,
        disabled: disabled,
        onChange: onChange,
        isShippingAddress: isShippingAddress
      });
    case VALID_FIELDS.ADDRESS_LINE_2:
      return /*#__PURE__*/_jsx(AddressLine2, {
        value: value,
        disabled: disabled,
        onChange: onChange
      });
    case VALID_FIELDS.LOCALITY:
      return /*#__PURE__*/_jsx(City, {
        value: value,
        onChange: onChange,
        disabled: disabled,
        isUS: isUS,
        isShippingAddress: isShippingAddress
      });
    case VALID_FIELDS.POSTAL_CODE:
      return /*#__PURE__*/_jsx(PostalCodeInput, {
        value: value,
        onChange: onChange,
        disabled: disabled,
        isShippingAddress: isShippingAddress
      });
    default:
      return null;
  }
};
export default FullAddressField;